import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Building2,
  Search,
  Plus,
  CheckCircle,
  XCircle,
  ExternalLink,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useTriggerReconciliation } from '@/hooks/usePayReconciliation';

interface Clinic {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  is_active: boolean;
  created_at: string;
  patient_counter: number;
  stripe_status: string | null;
}

export function AdminClinics() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const triggerReconciliation = useTriggerReconciliation();
  const [refreshingClinicId, setRefreshingClinicId] = useState<string | null>(null);

  const { data: clinics, isLoading } = useQuery({
    queryKey: ['admin-clinics'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clinics')
        .select(`
          id,
          name,
          email,
          phone,
          is_active,
          created_at,
          patient_counter,
          legacy_clinic_stripe_accounts(status)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data ?? []).map((c) => ({
        ...c,
        stripe_status: (c.legacy_clinic_stripe_accounts as { status: string }[] | null)?.[0]?.status ?? null,
      })) as Clinic[];
    },
  });

  const filteredClinics = clinics?.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('admin.clinics.title')}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('admin.clinics.subtitle')}
          </p>
        </div>
        <Button disabled>
          <Plus className="mr-2 h-4 w-4" />
          {t('admin.clinics.add_clinic')}
        </Button>
      </div>

      {/* Search */}
      <div className="mb-6 flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            type="text"
            placeholder={t('admin.clinics.search_placeholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('admin.clinics.col_clinic')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('admin.clinics.col_contact')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('admin.clinics.col_status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('admin.clinics.col_stripe')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('admin.clinics.col_patients')}
                </th>
                <th className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredClinics?.map((clinic) => (
                <tr key={clinic.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
                        <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{clinic.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(clinic.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                    <div>{clinic.email ?? '-'}</div>
                    <div>{clinic.phone ?? '-'}</div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    {clinic.is_active ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-400">
                        <CheckCircle className="h-3 w-3" />
                        {t('admin.clinics.active')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-800 dark:bg-red-900/30 dark:text-red-400">
                        <XCircle className="h-3 w-3" />
                        {t('admin.clinics.inactive')}
                      </span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    {clinic.stripe_status === 'enabled' ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-400">
                        <CheckCircle className="h-3 w-3" />
                        {t('admin.clinics.stripe_enabled')}
                      </span>
                    ) : clinic.stripe_status ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                        {clinic.stripe_status}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                    {clinic.patient_counter}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-right text-sm">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        type="button"
                        title="Refresh Pay status"
                        onClick={() => {
                          setRefreshingClinicId(clinic.id);
                          triggerReconciliation.mutate(
                            { clinic_id: clinic.id },
                            {
                              onSettled: () => setRefreshingClinicId(null),
                            },
                          );
                        }}
                        disabled={refreshingClinicId === clinic.id}
                        className="inline-flex items-center gap-1 text-purple-600 hover:text-purple-700 disabled:opacity-40 dark:text-purple-400 dark:hover:text-purple-300"
                      >
                        {refreshingClinicId === clinic.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3.5 w-3.5" />
                        )}
                        <span className="text-xs">Pay</span>
                      </button>
                      <Link
                        to={`/settings/clinics/${clinic.id}`}
                        className="inline-flex items-center gap-1 text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300"
                      >
                        {t('admin.clinics.view')}
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredClinics?.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                    {t('admin.clinics.no_clinics')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Stats */}
      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.clinics.total_clinics')}</p>
          <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">{clinics?.length ?? 0}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.clinics.active_clinics')}</p>
          <p className="mt-1 text-2xl font-bold text-green-600 dark:text-green-400">
            {clinics?.filter((c) => c.is_active).length ?? 0}
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.clinics.stripe_connected')}</p>
          <p className="mt-1 text-2xl font-bold text-purple-600 dark:text-purple-400">
            {clinics?.filter((c) => c.stripe_status === 'enabled').length ?? 0}
          </p>
        </div>
      </div>
    </div>
  );
}
